import { createCache } from 'async-cache-dedupe';

// src/indexer/subapis/achievement.ts
var AchievementIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of achievements owned by a specific address.
   * @category Achievement
   * @param characterId - The id of the character.
   * @param options - The options to send to the indexer.
   * @returns The list of achievements.
   */
  getMany(characterId, { status } = {}) {
    const url = `/characters/${characterId}/achievements`;
    return this.base.fetch(url, {
      params: { status }
    });
  }
  /**
   * This asks the indexer to mint an achievement for a specific character.
   * @category Achievement
   * @param characterId - The id of the character.
   * @param achievementId - The token id of the achievement.
   * @returns The achievement minted.
   */
  mint(characterId, achievementId) {
    const url = `/characters/${characterId}/achievements/${achievementId}`;
    return this.base.fetch(url, { method: "POST" });
  }
};

// src/indexer/utils.ts
var headersScope;
function getHeadersScope() {
  return headersScope;
}
function withHeaders(fn, headers) {
  const ensure = () => {
    headersScope = headers;
  };
  ensure();
  const ret = fn(ensure);
  headersScope = void 0;
  return ret;
}
function createSearchParamsString(params) {
  return new URLSearchParams(
    Object.entries(params).filter(
      (entry) => entry[1] !== void 0 && entry[1] !== null
    ).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)).flatMap(
      ([key, values]) => Array.isArray(values) ? values.map((value) => [key, value.toString()]) : [[key, values.toString()]]
    )
  ).toString();
}

// src/indexer/subapis/base.ts
var BaseIndexer = class {
  constructor(endpointOrOptions) {
    this.endpointOrOptions = endpointOrOptions;
    if (typeof endpointOrOptions === "string") {
      this.endpoint = endpointOrOptions;
    } else if (typeof endpointOrOptions === "object") {
      this.endpoint = endpointOrOptions.endpoint ?? this.endpoint;
      this.fetchOptions = endpointOrOptions.fetchOptions ?? this.fetchOptions;
    } else if (typeof endpointOrOptions !== "undefined") {
      throw new TypeError(
        `Invalid argument: ${JSON.stringify(
          endpointOrOptions
        )}. It must be a string or an object.`
      );
    }
  }
  /** The indexer endpoint */
  endpoint = "https://indexer.crossbell.io/v1";
  /** The options to send to the fetch function. */
  fetchOptions = {};
  #cache = createCache({
    ttl: 0,
    storage: { type: "memory" }
  }).define(
    "fetch",
    {
      serialize(args) {
        const url = args[0];
        const options = args[1];
        const body = options?.body;
        const headers = Object.fromEntries(
          options?.headers?.entries()
        );
        return {
          url,
          ...options,
          body,
          headers
        };
      }
    },
    (args) => {
      return fetch.call(globalThis, ...args);
    }
  );
  fetch(url, { params, data, token, ...options } = {}) {
    const newUrl = url + (params ? `?${createSearchParamsString(params)}` : "");
    const headers = new Headers({
      ...this.fetchOptions.headers,
      ...options.headers,
      ...getHeadersScope()
    });
    let body;
    if (data) {
      body = JSON.stringify(data);
      headers.set("Content-Type", "application/json");
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const fetched = typeof this.endpointOrOptions === "object" && this.endpointOrOptions.experimentalRequestDedupe && (!options.method || options.method === "GET") ? this.#cache.fetch([
      this.endpoint + newUrl,
      {
        ...this.fetchOptions,
        body,
        ...options,
        headers
      }
    ]) : fetch(this.endpoint + newUrl, {
      ...this.fetchOptions,
      body,
      ...options,
      headers
    });
    return fetched.then(async (r) => {
      const res = r.clone();
      if (!res.ok) {
        return Promise.reject(
          new Error(
            `Request failed, status code: ${r.status}
Response:
${await res.text()}`
          )
        );
      }
      return res.json();
    });
  }
};

// src/indexer/subapis/character.ts
var CharacterIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of characters owned by a specific address.
   * @category Character
   * @param address - The address of the character owner.
   * @param options - The options to send to the indexer.
   * @returns The list of characters.
   */
  getMany(address, {
    primary = void 0,
    limit = 20,
    cursor
  } = {}) {
    const url = `/addresses/${address}/characters`;
    return this.base.fetch(url, {
      params: { primary, limit, cursor }
    });
  }
  /**
   * This returns the primary character owned by a specific address;
   * null if none exists.
   * @category Character
   * @param address - The address of the character owner.
   * @returns The primary character.
   */
  async getPrimary(address) {
    const res = await this.getMany(address, { limit: 1, primary: true });
    return res.list?.[0] ?? null;
  }
  /**
   * This returns a character by id; null if none exists.
   * @category Character
   * @param characterId - The id of the character.
   * @returns The character.
   */
  get(characterId) {
    const url = `/characters/${characterId}`;
    return this.base.fetch(url);
  }
  /**
   * This returns a character by handle; null if none exists.
   * @category Character
   * @param handle - The handle of the character.
   * @returns The character.
   */
  getByHandle(handle) {
    const url = `/handles/${handle}/character`;
    return this.base.fetch(url);
  }
  /**
   * This checks if a handle exists.
   *
   * @category Character
   * @param handle - The handle to be checked.
   * @returns Whether the handle exists.
   */
  async checkIfHandleExists(handle) {
    const res = await this.getByHandle(handle);
    return res !== null;
  }
};

// src/indexer/subapis/feed.ts
var FeedIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of feeds.
   *
   * @category Feed
   * @param characterId - The characterId of the feed owner.
   * @param options - The options to send to the indexer.
   * @returns The list of feeds.
   */
  getManyByCharacter(characterId, {
    type,
    limit,
    cursor
  } = {}) {
    const url = `/characters/${characterId}/feed`;
    return this.base.fetch(url, {
      params: {
        type,
        limit,
        cursor
      }
    });
  }
  /**
   * This returns a list of following's feeds.
   *
   * @category Feed
   * @param characterId - The characterId of the feed owner.
   * @param options - The options to send to the indexer.
   * @returns The list of feeds.
   */
  getManyByCharacterFollowing(characterId, {
    type,
    limit,
    cursor
  } = {}) {
    const url = `/characters/${characterId}/feed/follow`;
    return this.base.fetch(url, {
      params: {
        type,
        limit,
        cursor
      }
    });
  }
  /**
   * This returns a specific feed.
   *
   * @category Feed
   * @param transactionHash - The transactionHash of the feed.
   * @param logIndex - The logIndex of the feed.
   * @returns The feed.
   */
  get(transactionHash, logIndex) {
    const url = `/feed/${transactionHash}/${logIndex}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/link.ts
var LinkIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of links starts from a specific character.
   *
   * Note that all links can only start from characters, but can end to characters, notes or other item types.
   *
   * @category Link
   * @param characterId - The characterId of the link owner.
   * @param options - The options to send to the indexer.
   * @returns The list of links.
   */
  getMany(characterId, options = {}) {
    const url = `/characters/${characterId}/links`;
    return this.base.fetch(url, {
      params: options
    });
  }
  /**
   * This returns a list of links from a linklist.
   *
   * Note that all links can only start from characters, but can end to characters, notes or other item types.
   *
   * @category Link
   * @param linklistId - The id of the linklist.
   * @param options - The options to send to the indexer.
   * @returns The list of links.
   */
  getManyByLinklistId(linklistId, options = {}) {
    const url = `/linklists/${linklistId}/links`;
    return this.base.fetch(url, {
      params: options
    });
  }
  /**
   * This returns a list of links ends at a specific character.
   *
   * @category Link
   * @param characterId - The characterId of the link ends to.
   * @param options - The options to send to the indexer.
   * @return The list of links.
   */
  getBacklinksOfCharacter(characterId, {
    limit = 20,
    cursor,
    linkType,
    order
  } = {}) {
    const url = `/characters/${characterId}/backlinks`;
    return this.base.fetch(url, {
      params: { limit, cursor, linkType, order }
    });
  }
  /**
   * This returns a list of links ends at a specific address.
   *
   * @category Link
   * @param address - The address of the link ends to.
   * @param options - The options to send to the indexer.
   * @return The list of links.
   */
  getBacklinksByAddress(address, {
    limit = 20,
    cursor,
    linkType,
    order
  } = {}) {
    const url = `/addresses/${address}/backlinks`;
    return this.base.fetch(url, {
      params: {
        limit,
        cursor,
        linkType,
        order
      }
    });
  }
  /**
   * This returns a list of links ends at a specific notes.
   *
   * @category Link
   * @param characterId - The characterId of the note owner.
   * @param noteId - The noteId of the link ends to.
   * @param options - The options to send to the indexer.
   * @return The list of links.
   */
  getBacklinksByNote(characterId, noteId, {
    limit = 20,
    cursor,
    linkType,
    order
  } = {}) {
    const url = `/notes/${characterId}/${noteId}/backlinks`;
    return this.base.fetch(url, {
      params: { limit, cursor, linkType, order }
    });
  }
  /**
   * This returns a list of links ends at a specific ERC721 token.
   *
   * @category Link
   * @param contractAddress - The contractAddress of the ERC721 token.
   * @param tokenId - The tokenId of the link ends to.
   * @param options - The options to send to the indexer.
   * @return The list of links.
   */
  getBacklinksByErc721(contractAddress, tokenId, {
    limit = 20,
    cursor,
    linkType,
    order
  } = {}) {
    const url = `/erc721s/${contractAddress}/${tokenId}/backlinks`;
    return this.base.fetch(url, {
      params: { limit, cursor, linkType, order }
    });
  }
  /**
   * This returns a list of links ends at a specific linklist.
   *
   * @category Link
   * @param linklistId - The linklistId of the link ends to.
   * @param options - The options to send to the indexer.
   * @return The list of links.
   */
  getBacklinksByLinklist(linklistId, {
    limit = 20,
    cursor,
    linkType,
    order
  } = {}) {
    const url = `/linklists/${linklistId}/backlinks`;
    return this.base.fetch(url, {
      params: {
        limit,
        cursor,
        linkType,
        order
      }
    });
  }
  /**
   * This returns a list of links ends at a specific uri.
   *
   * @category Link
   * @param uri - The uri of the link ends to.
   * @param options - The options to send to the indexer.
   * @return The list of links.
   */
  getBacklinksByAnyUri(uri, {
    limit = 20,
    cursor,
    linkType,
    order
  } = {}) {
    const url = `/anyuris/${uri}/backlinks`;
    return this.base.fetch(url, {
      params: {
        limit,
        cursor,
        linkType,
        order
      }
    });
  }
};

// src/indexer/subapis/link_module.ts
var LinkModuleIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of link modules.
   *
   * @category LinkModule
   * @param options - The options to send to the indexer.
   * @returns The list of link modules.
   */
  getMany({
    targetItemType,
    toAddress,
    toCharacterId,
    toNoteId,
    toContractAddress,
    toTokenId,
    toLinklistId,
    limit,
    cursor
  } = {}) {
    const url = "/link-modules";
    return this.base.fetch(url, {
      params: {
        targetItemType,
        toAddress,
        toCharacterId,
        toNoteId,
        toContractAddress,
        toTokenId,
        toLinklistId,
        limit,
        cursor
      }
    });
  }
  /**
   * This returns a specific link module.
   *
   * @category LinkModule
   * @param targetItemType - The targetItemType of the link module.
   * @param linkValue - The linkValue of the link module.
   * @returns The link module.
   */
  get(targetItemType, linkValue) {
    const url = `/link-modules/${targetItemType}/${linkValue}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/linklist.ts
var LinklistIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of linklists of a specific character.
   * @category Linklist
   * @param characterId - The characterId of the linklists owner.
   * @param options - The options to send to the indexer.
   * @returns The list of linklist.
   */
  getMany(characterId, {
    linkType,
    limit = 20,
    cursor
  } = {}) {
    const url = `/characters/${characterId}/linklists`;
    return this.base.fetch(url, {
      params: {
        linkType,
        limit,
        cursor
      }
    });
  }
  /**
   * This returns a linklist by id; null if none exists.
   * @category Linklist
   * @param linklistId - The id of the linklist.
   * @returns The character.
   */
  get(linklistId) {
    const url = `/linklists/${linklistId}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/metadata.ts
var MetadataIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * (Re)sync a character's metadata.
   *
   * @param characterId - the character id
   */
  syncForCharacter(characterId) {
    const url = `/characters/${characterId}/metadata/sync`;
    return this.base.fetch(url);
  }
  /**
   * (Re)sync a notes's metadata.
   *
   * @param characterId - the character id of the note's owner
   * @param noteId - the note id
   */
  syncForNote(characterId, noteId) {
    const url = `/notes/${characterId}/${noteId}/metadata/sync`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/mint_module.ts
var MintModuleIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of mint modules.
   *
   * @category MintModule
   * @param options - The options to send to the indexer.
   * @returns The list of mint modules.
   */
  getMany({
    targetItemType,
    toCharacterId,
    toNoteId,
    limit,
    cursor
  } = {}) {
    const url = "/mint-modules";
    return this.base.fetch(url, {
      params: {
        targetItemType,
        toCharacterId,
        toNoteId,
        limit,
        cursor
      }
    });
  }
  /**
   * This returns a specific mint module.
   *
   * @category MintModule
   * @param targetItemType - The targetItemType of the mint module.
   * @param linkValue - The linkValue of the mint module.
   * @returns The mint module.
   */
  get(targetItemType, linkValue) {
    const url = `/mint-modules/${targetItemType}/${linkValue}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/minted_note.ts
var MintedNoteIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of minted notes.
   *
   * @category MintedNote
   * @param address - The owner of the minted notes.
   * @param options - The options to send to the indexer.
   * @returns The list of minted notes.
   */
  getManyOfAddress(address, {
    noteCharacterId,
    noteId,
    variant,
    limit,
    cursor,
    order
  } = {}) {
    const url = `/addresses/${address}/minted/notes`;
    return this.base.fetch(url, {
      params: {
        noteCharacterId,
        noteId,
        variant,
        limit,
        cursor,
        order
      }
    });
  }
  /**
   * This returns a list of minted notes.
   *
   * @category MintedNote
   * @param characterId - The characterId of the note minted.
   * @param noteId - The noteId of the note minted.
   * @param options - The options to send to the indexer.
   * @returns The list of minted notes.
   */
  getManyOfNote(characterId, noteId, {
    owner,
    limit,
    cursor,
    order
  } = {}) {
    const url = `/notes/${characterId}/${noteId}/minted`;
    return this.base.fetch(url, {
      params: {
        owner,
        limit,
        cursor,
        order
      }
    });
  }
  /**
   * This returns a specific minted note.
   *
   * @category MintedNote
   * @param contractAddress - The contractAddress of the minted note.
   * @param tokenId - The tokenId of the minted note.
   * @returns The minted note.
   */
  get(contractAddress, tokenId) {
    const url = `/minted/notes/${contractAddress}/${tokenId}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/newbie.ts
var NewbieIndexer = class {
  constructor(base) {
    this.base = base;
  }
  token;
  /**
   * Ask for a verify-code to be sent to an email box for registration
   *
   * @category Newbie
   */
  requestSignUpEmail(email) {
    const url = "/newbie/account/signup/email";
    return this.base.fetch(url, {
      method: "POST",
      data: { email }
    });
  }
  /**
   * Check if the verify-code is valid for email registration
   * @category Newbie
   */
  verifySignUpCode(email, code) {
    const url = "/newbie/account/signup/email/verify";
    return this.base.fetch(url, {
      method: "POST",
      data: { email, code }
    });
  }
  /**
   * Register an new email user
   * @category Newbie
   * @returns The token of the new user
   */
  async signUp(options) {
    const url = "/newbie/account/signup";
    const { token } = await this.base.fetch(url, {
      method: "PUT",
      data: options
    });
    this.token = token;
    return token;
  }
  /**
   * Log in as an email user
   * @category Newbie
   * @returns The token of the user
   */
  async signIn(email, password) {
    const url = "/newbie/account/signin";
    const { token } = await this.base.fetch(url, {
      method: "POST",
      data: { email, password }
    });
    this.token = token;
    return token;
  }
  /**
   * Ask for a verify-code to be sent to an email box for password reset
   * @category Newbie
   */
  requestResetPasswordEmail(email) {
    const url = "/v1/newbie/account/reset-password/email";
    return this.base.fetch(url, {
      method: "POST",
      data: { email }
    });
  }
  /**
   * Check if the verify-code is valid for password reset
   * @category Newbie
   */
  verifyResetPasswordCode(email, code) {
    const url = "/newbie/account/reset-password/email/verify";
    return this.base.fetch(url, {
      method: "POST",
      data: { email, code }
    });
  }
  /**
   * Reset password
   * @category Newbie
   */
  resetPassword(options) {
    const url = "/newbie/account/reset-password";
    return this.base.fetch(url, {
      method: "POST",
      data: options
    });
  }
  /**
   * Get current user information
   */
  getAccount() {
    const url = "/newbie/account";
    return this.base.fetch(url, { token: this.token });
  }
  /**
   * Delete current user information
   */
  async deleteAccount() {
    const url = "/newbie/account";
    await this.base.fetch(url, { method: "DELETE", token: this.token });
  }
  linkNote({
    noteId,
    characterId,
    linkType,
    data
  }) {
    return this.base.fetch(
      `/newbie/contract/links/notes/${characterId}/${noteId}/${linkType}`,
      { method: "PUT", data: { data }, token: this.token }
    );
  }
  unlinkNote({
    noteId,
    characterId,
    linkType
  }) {
    return this.base.fetch(
      `/newbie/contract/links/notes/${characterId}/${noteId}/${linkType}`,
      { method: "DELETE", token: this.token }
    );
  }
  linkCharacter({
    toCharacterId,
    linkType,
    data
  }) {
    return this.base.fetch(
      `/newbie/contract/links/characters/${toCharacterId}/${linkType}`,
      { method: "PUT", data: { data }, token: this.token }
    );
  }
  linkCharacters({
    toCharacterIds,
    toAddresses,
    linkType,
    data
  }) {
    return this.base.fetch(
      "/newbie/contract/links/characters",
      {
        method: "PUT",
        data: { data, linkType, toCharacterIds, toAddresses },
        token: this.token
      }
    );
  }
  unlinkCharacter({
    toCharacterId,
    linkType
  }) {
    return this.base.fetch(
      `/newbie/contract/links/characters/${toCharacterId}/${linkType}`,
      { method: "DELETE", token: this.token }
    );
  }
  putNote(data) {
    return this.base.fetch(
      "/newbie/contract/notes",
      { method: "PUT", token: this.token, data }
    );
  }
  updateNote({
    noteId,
    metadata
  }) {
    return this.base.fetch(
      `/newbie/contract/notes/${noteId}/metadata`,
      { method: "POST", token: this.token, data: { metadata } }
    );
  }
  deleteNote({ noteId }) {
    return this.base.fetch(
      `/newbie/contract/notes/${noteId}`,
      { method: "DELETE", token: this.token }
    );
  }
  /**
   * Refill the balance of csb (once per day)
   */
  refillBalance() {
    return this.base.fetch("/newbie/account/balance/refill", { method: "POST", token: this.token });
  }
  updateHandle(handle) {
    return this.base.fetch("/newbie/contract/characters/me/handle", {
      method: "POST",
      token: this.token,
      data: { handle }
    });
  }
  updateCharactersMetadata({
    metadata,
    mode = "merge"
  }) {
    return this.base.fetch(
      "/newbie/contract/characters/me/metadata",
      {
        method: "POST",
        data: { metadata, mode },
        token: this.token
      }
    );
  }
  tipCharacter({
    characterId,
    amount
  }) {
    return this.base.fetch(
      "/newbie/contract/tips",
      {
        method: "POST",
        data: { characterId, amount },
        token: this.token
      }
    );
  }
  tipCharacterForNote({
    characterId,
    noteId,
    amount
  }) {
    return this.base.fetch(
      "/newbie/contract/tips",
      {
        method: "POST",
        data: { characterId, noteId, amount },
        token: this.token
      }
    );
  }
  /**
   * Get proofs for withdraw to call `withdraw` contract method on client
   */
  getWithdrawProof() {
    return this.base.fetch(
      "/newbie/account/withdraw/proof",
      { method: "GET", token: this.token }
    );
  }
};

// src/indexer/subapis/note.ts
var NoteIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of notes.
   *
   * @category Note
   * @param params - The options of note query.
   * @returns The list of notes.
   */
  getMany(params = {}) {
    const url = "/notes";
    return this.base.fetch(url, { params });
  }
  /**
   * This returns a list of notes of a list of characters followed by a character.
   *
   * @category Note
   * @param characterId - The characterId.
   * @param params - The options of note query.
   * @returns
   */
  getManyOfCharacterFollowing(characterId, params = {}) {
    const url = `/characters/${characterId}/notes/following`;
    return this.base.fetch(url, { params });
  }
  /**
   * This returns a specific note.
   *
   * @category Note
   * @param characterId - The characterId of the notes owner.
   * @param noteId - The noteId of the note to get.
   * @returns The note.
   */
  get(characterId, noteId) {
    const url = `/characters/${characterId}/notes/${noteId}`;
    return this.base.fetch(url);
  }
  /**
   * This returns all distinct tags of notes owned by a character.
   *
   * @category Note
   * @param characterId - The characterId of the notes owner.
   * @param options - The options of note query.
   * @returns The list of tags.
   */
  getDistinctTagsOfCharacter(characterId, {
    sources
  } = {}) {
    const url = `/characters/${characterId}/notes/tags`;
    return this.base.fetch(url, {
      params: {
        sources
      }
    });
  }
  /**
   * This returns all distinct tags of notes owned by a character.
   *
   * @category Note
   * @param characterId - The characterId of the notes owner.
   * @param options - The options of note query.
   * @returns The list of tags.
   */
  getDistinctSourcesOfCharacter(characterId, {
    tags
  } = {}) {
    const url = `/characters/${characterId}/notes/sources`;
    return this.base.fetch(url, {
      params: {
        tags
      }
    });
  }
};

// src/indexer/subapis/notification.ts
var NotificationIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of notifications.
   *
   * @category Notification
   * @param characterId - The characterId of the notification owner.
   * @param options - The options to send to the indexer.
   * @returns The list of notifications.
   */
  getMany(characterId, {
    type,
    includeCharacterMetadata,
    includeSelfInvoked,
    includeIsRead,
    read,
    limit,
    cursor
  } = {}) {
    const url = `/characters/${characterId}/notifications`;
    return this.base.fetch(url, {
      params: {
        type,
        includeCharacterMetadata,
        includeSelfInvoked,
        includeIsRead,
        read,
        limit,
        cursor
      }
    });
  }
  /**
   * This marks all notifications as read.
   *
   * @category Notification
   * @param characterId - The characterId of the notification owner.
   * @returns The latest notification date string.
   */
  markAllAsRead(characterId) {
    const url = `/characters/${characterId}/notifications/read`;
    return this.base.fetch(url, {
      method: "POST"
    });
  }
  /**
   * This returns a specific notification.
   *
   * @category Notification
   * @param characterId - The characterId of the notification owner.
   * @param transactionHash - The transactionHash of the notification.
   * @param logIndex - The logIndex of the notification.
   * @returns The feed.
   */
  get(characterId, transactionHash, logIndex, {
    includeCharacterMetadata
  } = {}) {
    const url = `/characters/${characterId}/notifications/${transactionHash}/${logIndex}`;
    return this.base.fetch(url, {
      params: {
        includeCharacterMetadata
      }
    });
  }
  /**
   * This returns the count of unread notifications.
   * @category Notification
   * @param characterId - The characterId of the notification owner.
   */
  getUnreadCount(characterId) {
    const url = `/characters/${characterId}/notifications/unread/count`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/operators.ts
var OperatorIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of operators for a specific character.
   * @category Operator
   * @param characterId - The id of the character.
   * @param options - The options to send to the indexer.
   * @returns The list of operators.
   */
  getManyForCharacter(characterId, {
    limit = 20,
    cursor
  } = {}) {
    const url = `/characters/${characterId}/operators`;
    return this.base.fetch(url, {
      params: { limit, cursor }
    });
  }
  /**
   * This returns the operator of a character; null if none exists.
   * @category Operator
   * @param characterId - The id of the character.
   * @param address - The address of the operator.
   * @returns The primary character.
   */
  getForCharacter(characterId, address) {
    const url = `/characters/${characterId}/operators/${address}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/search.ts
var SearchIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This searches for characters on the crossbell contract.
   *
   * @category Search
   * @param query - The query to send to the indexer.
   * @param options - The options to send to the indexer.
   * @returns
   */
  characters(query, options) {
    const url = "/characters/search";
    return this.base.fetch(url, {
      params: { q: query, ...options }
    });
  }
  /**
   * This searches for notes on the crossbell contract.
   *
   * @category Search
   * @param query - The query to send to the indexer.
   * @param options - The options to send to the indexer.
   * @returns
   */
  notes(query, options) {
    const url = "/notes/search";
    return this.base.fetch(url, {
      params: {
        q: query,
        ...options
      }
    });
  }
};

// src/indexer/subapis/siwe.ts
var SiweIndexer = class {
  constructor(base) {
    this.base = base;
  }
  token;
  async signIn(signer) {
    const address = await signer.getAddress();
    if (!address)
      throw new Error(`SignInError: invalid address ${address}`);
    const { message } = await this.base.fetch(
      "/siwe/challenge",
      {
        method: "POST",
        data: {
          address,
          domain: typeof window !== "undefined" && window.location.host || "crossbell.io",
          uri: typeof window !== "undefined" && window.location.origin || "https://crossbell.io",
          statement: "Sign in with Crossbell to the app."
        }
      }
    );
    const { token } = await this.base.fetch("/siwe/login", {
      method: "POST",
      data: {
        address,
        signature: await signer.signMessage(message)
      }
    });
    this.token = token;
    return { token };
  }
  getAccount() {
    return this.base.fetch("/siwe/account", {
      method: "GET",
      token: this.token
    });
  }
  getBalance() {
    return this.base.fetch("/siwe/account/balance", {
      method: "GET",
      token: this.token
    });
  }
  updateMetadata({
    mode = "merge",
    characterId,
    metadata
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/metadata`,
      {
        method: "POST",
        token: this.token,
        data: { metadata, mode }
      }
    );
  }
  linkNote({
    fromCharacterId,
    noteId,
    characterId,
    linkType,
    data
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${fromCharacterId}/links/notes/${characterId}/${noteId}/${linkType}`,
      { method: "PUT", token: this.token, data: { data } }
    );
  }
  unlinkNote({
    fromCharacterId,
    noteId,
    characterId,
    linkType
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${fromCharacterId}/links/notes/${characterId}/${noteId}/${linkType}`,
      { method: "DELETE", token: this.token }
    );
  }
  linkCharacter({
    characterId,
    toCharacterId,
    linkType,
    data
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/links/characters/${toCharacterId}/${linkType}`,
      { method: "PUT", token: this.token, data: { data } }
    );
  }
  linkCharacters({
    characterId,
    toCharacterIds,
    toAddresses,
    linkType,
    data
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/links/characters`,
      {
        method: "PUT",
        token: this.token,
        data: { data, linkType, toCharacterIds, toAddresses }
      }
    );
  }
  unlinkCharacter({
    characterId,
    toCharacterId,
    linkType
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/links/characters/${toCharacterId}/${linkType}`,
      { method: "DELETE", token: this.token }
    );
  }
  putNote({
    characterId,
    ...body
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/notes`,
      {
        method: "PUT",
        token: this.token,
        data: body
      }
    );
  }
  updateNote({
    characterId,
    noteId,
    metadata
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/notes/${noteId}/metadata`,
      {
        method: "POST",
        token: this.token,
        data: { metadata }
      }
    );
  }
  deleteNote({
    characterId,
    noteId
  }) {
    return this.base.fetch(
      `/siwe/contract/characters/${characterId}/notes/${noteId}`,
      {
        method: "DELETE",
        token: this.token
      }
    );
  }
  mintNote({ characterId, noteId }) {
    return this.base.fetch(`/siwe/contract/characters/${characterId}/notes/${noteId}/minted`, {
      method: "PUT",
      token: this.token,
      data: {}
    });
  }
};

// src/indexer/subapis/stat.ts
var StatIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * Get stat of a character.
   * @category Stat
   * @param characterId - The id of the character.
   * @returns The stat of the character.
   */
  getForCharacter(characterId) {
    const url = `/stat/characters/${characterId}`;
    return this.base.fetch(url);
  }
  /**
   * Get stat of a note.
   * @category Stat
   * @param characterId - The id of the character.
   * @param noteId - The id of the note.
   * @returns The stat of the note.
   */
  getForNote(characterId, noteId) {
    const url = `/stat/notes/${characterId}/${noteId}`;
    return this.base.fetch(url);
  }
};

// src/indexer/subapis/tip.ts
var TipIndexer = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns a list of tips.
   *
   * @category Tip
   * @param characterId - The characterId of the feed owner.
   * @param options - The options to send to the indexer.
   * @returns The list of tips.
   */
  getMany({
    characterId,
    toCharacterId,
    toNoteId,
    tokenAddress,
    includeZeroAmount,
    includeMetadata,
    limit,
    cursor
  } = {}) {
    const url = "/tips";
    return this.base.fetch(url, {
      params: {
        characterId,
        toCharacterId,
        toNoteId,
        tokenAddress,
        includeZeroAmount,
        includeMetadata,
        limit,
        cursor
      }
    });
  }
  /**
   * This returns a specific tip.
   *
   * @category Tip
   * @param transactionHash - The transactionHash of the tip.
   * @param logIndex - The logIndex of the tip.
   * @returns The tip.
   */
  get(transactionHash, logIndex) {
    const url = `/tips/${transactionHash}/${logIndex}`;
    return this.base.fetch(url);
  }
};

// src/indexer/indexer.ts
var Indexer = class extends BaseIndexer {
  link = new LinkIndexer(this);
  linklist = new LinklistIndexer(this);
  note = new NoteIndexer(this);
  character = new CharacterIndexer(this);
  search = new SearchIndexer(this);
  feed = new FeedIndexer(this);
  mintedNote = new MintedNoteIndexer(this);
  metadata = new MetadataIndexer(this);
  achievement = new AchievementIndexer(this);
  notification = new NotificationIndexer(this);
  operator = new OperatorIndexer(this);
  tip = new TipIndexer(this);
  linkModule = new LinkModuleIndexer(this);
  mintModule = new MintModuleIndexer(this);
  newbie = new NewbieIndexer(this);
  siwe = new SiweIndexer(this);
  stat = new StatIndexer(this);
};
function createIndexer(endpointOrOptions) {
  return new Indexer(endpointOrOptions);
}

export { Indexer, createIndexer, withHeaders };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-C64VIAZW.mjs.map